import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { shopApi, formatApiError } from "../../lib/shopApi";
import { PasswordInput } from "../../components/shop/PasswordInput";

const inputCls = "w-full bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#C9A24B] transition-colors";

const AccountAuthPage = ({ mode }) => {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await shopApi.post(`/account/${isLogin ? "login" : "register"}`, form);
      localStorage.setItem("cc_customer_token", r.data.token);
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err, "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ShopLayout title={isLogin ? "Sign In | Loctician Carlocs" : "Create Account | Loctician Carlocs"}>
      <div className="max-w-sm mx-auto px-5 py-14 md:py-20" data-testid={isLogin ? "account-login-page" : "account-register-page"}>
        <div className="text-center mb-8">
          <User className="w-9 h-9 text-[#C9A24B] mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">{isLogin ? "Welcome Back" : "Create Account"}</h1>
          <p className="text-[#F5EFE3]/50 text-sm">
            {isLogin ? "Sign in to track your orders." : "Track orders and check out faster."}
          </p>
        </div>

        <form onSubmit={submit} className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 space-y-4">
          {!isLogin && (
            <>
              <input value={form.name} onChange={set("name")} required placeholder="Full name" data-testid="account-name" className={inputCls} />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="account-phone" className={inputCls} />
            </>
          )}
          <input type="email" value={form.email} onChange={set("email")} required placeholder="Email" data-testid="account-email" className={inputCls} />
          <PasswordInput value={form.password} onChange={set("password")} required placeholder={isLogin ? "Password" : "Password (min 6 characters)"} data-testid="account-password-input" toggleTestId="account-password-toggle" className={`${inputCls} pr-12`} />
          {isLogin && (
            <p className="text-right -mt-1">
              <Link to="/account/forgot-password" className="text-[#F5EFE3]/50 hover:text-[#C9A24B] text-xs transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </p>
          )}
          {error && <p className="text-red-400 text-sm" data-testid="account-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="account-submit-btn"
            className="w-full bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
          >
            {busy ? "Please wait…" : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-[#F5EFE3]/40 text-sm mt-6">
          {isLogin ? (
            <>No account? <Link to="/account/register" className="text-[#C9A24B] underline underline-offset-4" data-testid="go-register-link">Create one</Link></>
          ) : (
            <>Already have one? <Link to="/account/login" className="text-[#C9A24B] underline underline-offset-4" data-testid="go-login-link">Sign in</Link></>
          )}
        </p>
        <p className="text-center text-[#F5EFE3]/40 text-sm mt-3">
          <Link to="/track" className="text-[#F5EFE3]/50 hover:text-[#C9A24B] transition-colors" data-testid="go-track-link">Track an order without an account</Link>
        </p>
      </div>
    </ShopLayout>
  );
};

export default AccountAuthPage;
